import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/authService';
import analyticsService from '../../services/analyticsService';

const Register = ({ onSuccess, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState(null);
  const [resendStatus, setResendStatus] = useState(null);
  const { loginWithGoogle } = useAuth();

  const handleGoogleSuccess = async (credentialResponse) => {
    setErrors({});
    try {
      await loginWithGoogle(credentialResponse.credential);
      analyticsService.trackRegistration('google');
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrors({ submit: error.message || 'Error al registrarse con Google' });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) {
      newErrors.name = 'El nombre es requerido';
    } else if (formData.name.length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const response = await authService.register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });

      // Mostrar pantalla de confirmación — el usuario debe verificar su correo
      analyticsService.trackRegistration('email');
      setRegistrationEmail(response.email || formData.email);
    } catch (error) {
      setErrors({
        submit: error.message || 'Error al crear la cuenta'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!registrationEmail) return;
    setResendStatus('loading');
    try {
      await authService.resendVerification(registrationEmail);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  // Pantalla de confirmación tras registro exitoso
  if (registrationEmail) {
    return (
      <div className="text-center">
        <div className="mb-4">
          <div className="w-16 h-16 bg-df-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-df-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-df-text mb-2">¡Revisa tu correo!</h2>
          <p className="text-df-muted mb-1">
            Enviamos un enlace de verificación a:
          </p>
          <p className="font-semibold text-df-success mb-4">{registrationEmail}</p>
          <p className="text-sm text-df-muted mb-6">
            Haz clic en el enlace del correo para activar tu cuenta. El enlace expira en 24 horas.
          </p>
        </div>

        {resendStatus === 'sent' && (
          <p className="text-sm text-df-success mb-3">Correo reenviado correctamente.</p>
        )}
        {resendStatus === 'error' && (
          <p className="text-sm text-df-danger mb-3">Error al reenviar. Intenta de nuevo.</p>
        )}

        <button
          onClick={handleResend}
          disabled={resendStatus === 'loading' || resendStatus === 'sent'}
          className="text-sm text-df-accent-hover hover:text-df-accent underline disabled:opacity-50 disabled:cursor-not-allowed mb-4 block mx-auto"
        >
          {resendStatus === 'loading' ? 'Reenviando...' : '¿No llegó el correo? Reenviar'}
        </button>

        <button
          onClick={onSwitchToLogin}
          className="text-sm text-df-muted hover:text-df-text"
        >
          Volver a iniciar sesión
        </button>
      </div>
    );
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-df-text mb-1">
            Nombre Completo
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-df-text focus:outline-none focus:ring-2 focus:ring-df-accent ${
              errors.name ? 'border-df-danger' : 'border-df-border'
            }`}
            placeholder="Tu nombre completo"
          />
          {errors.name && <p className="mt-1 text-sm text-df-danger">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-df-text mb-1">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-df-text focus:outline-none focus:ring-2 focus:ring-df-accent ${
              errors.email ? 'border-df-danger' : 'border-df-border'
            }`}
            placeholder="tu@email.com"
          />
          {errors.email && <p className="mt-1 text-sm text-df-danger">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-df-text mb-1">
            Contraseña
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-df-text focus:outline-none focus:ring-2 focus:ring-df-accent ${
              errors.password ? 'border-df-danger' : 'border-df-border'
            }`}
            placeholder="••••••••"
          />
          {errors.password && <p className="mt-1 text-sm text-df-danger">{errors.password}</p>}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-df-text mb-1">
            Confirmar Contraseña
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md text-df-text focus:outline-none focus:ring-2 focus:ring-df-accent ${
              errors.confirmPassword ? 'border-df-danger' : 'border-df-border'
            }`}
            placeholder="••••••••"
          />
          {errors.confirmPassword && <p className="mt-1 text-sm text-df-danger">{errors.confirmPassword}</p>}
        </div>

        {errors.submit && (
          <div className="bg-df-danger/10 border border-df-danger rounded-md p-3">
            <p className="text-sm text-df-danger">{errors.submit}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className={`w-full py-2 px-4 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-df-accent focus:ring-offset-2 ${
            isLoading
              ? 'bg-df-surface3 cursor-not-allowed'
              : 'bg-df-accent-strong hover:bg-df-accent-deep text-white'
          }`}
        >
          {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
        </button>
      </form>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-df-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-df-surface text-df-muted">O regístrate con</span>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setErrors({ submit: 'Error al registrarse con Google' })}
            text="signup_with"
            shape="rectangular"
            width="100%"
            locale="es"
          />
        </div>
      </div>

      <div className="mt-4 text-center">
        <p className="text-sm text-df-muted">
          ¿Ya tienes una cuenta?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-df-accent-hover hover:text-df-accent font-medium"
          >
            Inicia sesión aquí
          </button>
        </p>
      </div>
    </div>
  );
};

export default Register;
